import type { JSX } from 'react';
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';
import type { AuditLogEntry } from '@echo-work-platform/shared-types';

export function AuditLog(): JSX.Element {
  const [expanded, setExpanded] = useState<string | null>(null);
  const log = useQuery({
    queryKey: ['/api/admin/audit-log'],
    queryFn: () => api.get<AuditLogEntry[]>('/api/admin/audit-log?limit=200'),
  });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Audit Log</h2>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Időpont</th>
              <th className="px-4 py-2 text-left">Művelet</th>
              <th className="px-4 py-2 text-left">Kimenetel</th>
              <th className="px-4 py-2 text-left">Cél típus</th>
              <th className="px-4 py-2 text-left">Cél ID</th>
              <th className="px-4 py-2 text-left">Végrehajtó</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-mono text-xs">
            {log.data?.map((e) => {
              const isOpen = expanded === e.id;
              return (
                <Fragment key={e.id}>
                  <tr
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                  >
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(e.timestamp)}</td>
                    <td className="px-4 py-2 font-semibold">{e.action}</td>
                    <td className="px-4 py-2">{e.outcome ?? '—'}</td>
                    <td className="px-4 py-2">{e.targetType ?? '—'}</td>
                    <td className="px-4 py-2">{e.targetId?.slice(0, 8) ?? '—'}</td>
                    {/* No 'system' fallback: an entry without an actor is usually one we failed to
                        attribute, not a machine action. The reason is in `after`, not guessable here. */}
                    <td className="px-4 py-2 font-sans">
                      {e.principalName ? (
                        <span title={e.principalEmail ?? undefined}>{e.principalName}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="px-4 py-3">
                        {!e.before && !e.after ? (
                          <div className="font-sans text-slate-500">
                            Ehhez a bejegyzéshez nincs rögzített adat.
                          </div>
                        ) : (
                          <div className="space-y-2 text-xs">
                            {e.before && (
                              <div>
                                <div className="mb-1 font-sans font-medium text-slate-600">Előtte</div>
                                <pre className="overflow-auto rounded border border-slate-200 bg-white p-3 font-mono text-[11px] whitespace-pre-wrap break-all">
                                  {JSON.stringify(e.before, null, 2)}
                                </pre>
                              </div>
                            )}
                            {e.after && (
                              <div>
                                <div className="mb-1 font-sans font-medium text-slate-600">Utána</div>
                                <pre className="overflow-auto rounded border border-slate-200 bg-white p-3 font-mono text-[11px] whitespace-pre-wrap break-all">
                                  {JSON.stringify(e.after, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {log.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  Nincs bejegyzés.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
